import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import {
    getDatabase, ref, onValue, set, get, push, update, serverTimestamp, off, onDisconnect, remove
} from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import {
    gamesAPI, tournamentsAPI, notifyAPI,
    walletAPI, getToken, getMongoId,
} from '../api/api'

// ─── Firebase config ───────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            "AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc",
    authDomain:        "clubswap-5a74a.firebaseapp.com",
    databaseURL:       "https://clubswap-5a74a-default-rtdb.firebaseio.com",
    projectId:         "clubswap-5a74a",
    storageBucket:     "clubswap-5a74a.firebasestorage.app",
    messagingSenderId: "274838532963",
    appId:             "1:274838532963:web:4044ba76f6192dcfdf5956",
}

let _app, _db, _auth
function getDB() {
    if (!_db) {
        _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db   = getDatabase(_app)
        _auth = getAuth(_app)
    }
    return _db
}
function getFirebaseAuth() { getDB(); return _auth }

// ─── Helpers ───────────────────────────────────────────────────────────────────
function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}
function fmtDiamond(n) { return `💎 ${n}` }

// ─── Modals ────────────────────────────────────────────────────────────────────

function RecordingReminderModal({ opponent, stake, gameType, onConfirm, onCancel, loading }) {
    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.modalIcon}>🎥</div>
                <h2 style={styles.modalTitle}>Record Your Gameplay</h2>
                <p style={styles.modalBody}>
                    Before you start, <strong>please record your screen</strong> during this match.
                </p>
                <div style={styles.reminderBox}>
                    <span style={styles.reminderDot}>⚠️</span>
                    <span style={styles.reminderText}>
                        If a dispute is raised, we may ask you to submit your recording as evidence.
                        Players who file unnecessary disputes may be penalised.
                    </span>
                </div>
                <div style={styles.matchPreview}>
                    <div style={styles.matchPreviewRow}>
                        <span style={styles.previewLabel}>Opponent</span>
                        <span style={styles.previewValue}>{opponent?.displayName || 'Unknown'}</span>
                    </div>
                    <div style={styles.matchPreviewRow}>
                        <span style={styles.previewLabel}>Game</span>
                        <span style={styles.previewValue}>{gameType === 'pool' ? '🎱 8 Ball Pool' : '♟️ Checkers'}</span>
                    </div>
                    <div style={styles.matchPreviewRow}>
                        <span style={styles.previewLabel}>Your entry</span>
                        <span style={styles.previewValue}>{fmtDiamond(stake)}</span>
                    </div>
                    <div style={styles.matchPreviewRow}>
                        <span style={styles.previewLabel}>Prize pool</span>
                        <span style={{ ...styles.previewValue, color: '#4ade80' }}>{fmtDiamond(stake * 2)}</span>
                    </div>
                </div>
                <p style={styles.confirmNote}>
                    By clicking <strong>Send Challenge</strong>, {fmtDiamond(stake)} will be deducted from your balance.
                    You'll get a full refund if opponent doesn't accept within <strong>3 minutes</strong>.
                </p>
                <div style={styles.modalButtons}>
                    <button style={styles.cancelBtn} onClick={onCancel} disabled={loading}>Cancel</button>
                    <button style={styles.confirmBtn} onClick={onConfirm} disabled={loading}>
                        {loading ? 'Sending…' : '🎥 I\'ll Record — Send Challenge'}
                    </button>
                </div>
            </div>
        </div>
    )
}

function SetAvailableModal({ currentUser, myMongoId, onSave, onClose }) {
    const [stake,    setStake]    = useState(100)
    const [gameType, setGameType] = useState('pool')
    const [saving,   setSaving]   = useState(false)
    const [error,    setError]    = useState('')

    const PRESETS = [50, 100, 250, 500]

    const handleSave = async () => {
        setSaving(true)
        setError('')
        try {
            // Balance check against MongoDB — authoritative
            if (getToken()) {
                const w = await walletAPI.balance()
                if (w.balance < stake) {
                    setError(`Not enough 💎. You have ${w.balance} diamonds.`)
                    setSaving(false)
                    return
                }
            }
            const db = getDB()
            const profileSnap = await get(ref(db, `users/${currentUser.uid}/profile`))
            const profile     = profileSnap.val() || {}
            const displayName = profile.displayName
                || currentUser.displayName
                || currentUser.email?.split('@')[0]
                || 'Player'

            const availPlayerRef = ref(db, `availablePlayers/${currentUser.uid}`)

            await Promise.all([
                set(ref(db, `users/${currentUser.uid}/presence`), {
                    online:               true,
                    lastSeen:             Date.now(),
                    availableToChallenge: true,
                    stake,
                    gameType,
                    inMatch:              false,
                    displayName,
                    mongoId:              myMongoId || null,
                }),
                // Write into the scoped index — only available players live here
                set(availPlayerRef, {
                    gameType,
                    stake,
                    displayName,
                    mongoId:   myMongoId || null,
                    level:     profile.level ?? 1,
                    updatedAt: Date.now(),
                }),
            ])
            // Auto-remove from index if client disconnects unexpectedly
            onDisconnect(availPlayerRef).remove()
            onSave()
        } catch (e) {
            setError('Failed to set available. Try again.')
        }
        setSaving(false)
    }

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <h2 style={styles.modalTitle}>Set Yourself Available</h2>
                <p style={{ ...styles.modalBody, marginBottom: 20 }}>
                    Other players will see you and can send a challenge.
                </p>
                <div style={styles.fieldGroup}>
                    <label style={styles.fieldLabel}>Game</label>
                    <div style={styles.gameToggle}>
                        {[{ id: 'pool', label: '🎱 8 Ball Pool' }, { id: 'checkers', label: '♟️ Checkers' }].map(g => (
                            <button key={g.id} style={gameType === g.id ? styles.gameToggleActive : styles.gameToggleBtn} onClick={() => setGameType(g.id)}>
                                {g.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={styles.fieldGroup}>
                    <label style={styles.fieldLabel}>Stake Amount</label>
                    <div style={styles.presetRow}>
                        {PRESETS.map(p => (
                            <button key={p} style={stake === p ? styles.presetActive : styles.presetBtn} onClick={() => setStake(p)}>
                                {fmtDiamond(p)}
                            </button>
                        ))}
                    </div>
                    <input type="number" min={10} value={stake} onChange={e => setStake(Number(e.target.value))} style={styles.input} placeholder="Custom amount" />
                </div>
                {error && <div style={styles.errorMsg}>{error}</div>}
                <div style={styles.modalButtons}>
                    <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
                    <button style={styles.confirmBtn} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : '✅ Set Available'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── MAIN HOME COMPONENT ───────────────────────────────────────────────────────
export default function Home() {
    const navigate = useNavigate()

    // Auth
    const [currentUser,   setCurrentUser]   = useState(null)
    const [myMongoId,     setMyMongoId]     = useState(null)

    // Online players (from Firebase presence)
    const [onlinePlayers,    setOnlinePlayers]    = useState([])
    const [availablePlayers, setAvailablePlayers] = useState([])

    // UI state
    const [activeTab,          setActiveTab]          = useState('pool')
    const [showSetAvail,       setShowSetAvail]       = useState(false)
    const [challengeTarget,    setChallengeTarget]    = useState(null)
    const [showRecordModal,    setShowRecordModal]    = useState(false)
    const [challengeLoading,   setChallengeLoading]   = useState(false)
    const [challengeError,     setChallengeError]     = useState('')
    const [userPresence,       setUserPresence]       = useState(null)

    // Tournaments — from MongoDB
    const [tournaments, setTournaments] = useState([])

    // Notifications
    const [notifications, setNotifications]   = useState([])
    const [showNotifPanel, setShowNotifPanel] = useState(false)
    const [incomingChallenges, setIncomingChallenges] = useState([])

    const presenceUnsub = useRef(null)
    const playersUnsub  = useRef(null)
    const balanceUnsub  = useRef(null)

    // ── Auth listener ─────────────────────────────────────────
    useEffect(() => {
        const auth = getFirebaseAuth()
        const unsub = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user)
            // Pick up mongoId from localStorage (set by UserAccount on login)
            setMyMongoId(getMongoId())
        })
        return () => unsub()
    }, [])

    // ── Own presence ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!currentUser) return
        const db = getDB()
        const pRef = ref(db, `users/${currentUser.uid}/presence`)
        const unsub = onValue(pRef, snap => setUserPresence(snap.val()))
        return () => off(pRef)
    }, [currentUser])

    // ── All players presence ───────────────────────────────────
    useEffect(() => {
        const db = getDB()
        const usersRef = ref(db, 'users')
        playersUnsub.current = onValue(usersRef, snap => {
            const data = snap.val() || {}
            const online = []
            const available = []
            Object.entries(data).forEach(([uid, userData]) => {
                if (!userData.presence) return
                const p = userData.presence
                if (!p.online) return
                const player = {
                    uid,
                    displayName: userData.profile?.displayName || p.displayName || 'Player',
                    level: userData.profile?.level ?? 1,
                    stake: p.stake ?? 100,
                    gameType: p.gameType ?? 'pool',
                    availableToChallenge: p.availableToChallenge && !p.inMatch,
                    inMatch: p.inMatch,
                    lastSeen: p.lastSeen,
                }
                online.push(player)
                if (player.availableToChallenge) available.push(player)
            })
            setOnlinePlayers(online)
            setAvailablePlayers(available)
        })
        return () => off(usersRef)
    }, [])

    // ── Tournaments listener ───────────────────────────────────
    useEffect(() => {
        const db = getDB()
        const tRef = ref(db, 'tournaments')
        const unsub = onValue(tRef, snap => {
            const data = snap.val() || {}
            const list = Object.entries(data)
                .map(([id, t]) => ({ id, ...t.meta }))
                .filter(t => t.status === 'open')
                .slice(0, 3)
            setTournaments(list)
        })
        return () => off(tRef)
    }, [])

    // ── Incoming challenge + notification listeners ──────────────
    useEffect(() => {
        if (!currentUser) return
        const db = getDB()
        const chalRef = ref(db, 'challenges')
        const unsub = onValue(chalRef, snap => {
            const data = snap.val() || {}
            const incoming = []
            const notifs = []
            const cutoff = Date.now() - 24 * 60 * 60 * 1000  // 24h ago

            Object.entries(data).forEach(([cid, c]) => {
                // Incoming pending challenges for this user
                if (
                    c.opponentUid === currentUser.uid &&
                    c.status === 'pending' &&
                    c.expiresAt > Date.now()
                ) {
                    incoming.push({ cid, ...c })
                }
                // 24h notifications — challenges involving this user
                const isInvolved = c.opponentUid === currentUser.uid || c.challengerUid === currentUser.uid
                if (isInvolved && c.createdAt > cutoff) {
                    notifs.push({
                        id: cid,
                        type: 'challenge',
                        title: c.opponentUid === currentUser.uid
                            ? `⚔️ ${c.challengerName} challenged you`
                            : `⚔️ You challenged ${c.opponentName}`,
                        sub: `${c.gameType === 'pool' ? '🎱' : '♟️'} · 💎 ${c.stake} · ${c.status}`,
                        link: `/challenge/${cid}`,
                        time: c.createdAt,
                        status: c.status,
                    })
                }
            })
            setIncomingChallenges(incoming)
            setNotifications(notifs.sort((a, b) => b.time - a.time))
        })
        return () => off(chalRef)
    }, [currentUser])

    // ── Browser notification permission ───────────────────────

    // ── Request notification permission ───────────────────────
    useEffect(() => {
        if (Notification.permission === 'default') Notification.requestPermission()
    }, [])

    // ── Set offline on unload ─────────────────────────────────
    useEffect(() => {
        if (!currentUser) return
        const db = getDB()
        const handleUnload = () => {
            set(ref(db, `users/${currentUser.uid}/presence/online`), false)
        }
        window.addEventListener('beforeunload', handleUnload)
        return () => window.removeEventListener('beforeunload', handleUnload)
    }, [currentUser])

    // ── Set online ────────────────────────────────────────────
    useEffect(() => {
        if (!currentUser) return
        const db = getDB()
        update(ref(db, `users/${currentUser.uid}/presence`), {
            online: true,
            lastSeen: Date.now(),
        })
    }, [currentUser])

    // ─────────────────────────────────────────────────────────
    //  CHALLENGE FLOW
    // ─────────────────────────────────────────────────────────

    const handleChallengeClick = useCallback((player) => {
        if (!currentUser) { navigate('/account'); return }
        if (player.uid === currentUser.uid) return
        setChallengeTarget(player)
        setChallengeError('')
        setShowRecordModal(true)
    }, [currentUser, navigate])

    const handleConfirmChallenge = useCallback(async () => {
        if (!challengeTarget || !currentUser) return
        setChallengeLoading(true)
        setChallengeError('')

        try {
            const db       = getDB()
            const stake    = challengeTarget.stake
            const gameType = challengeTarget.gameType

            // 1. Create match in MongoDB — this escrows the challenger's stake atomically
            const match = await gamesAPI.createChallenge(gameType, stake)
            const mongoMatchId = match._id

            // 2. Get challenger's display name
            const myProfileSnap = await get(ref(db, `users/${currentUser.uid}/profile`))
            const myName = myProfileSnap.val()?.displayName
                || currentUser.displayName
                || currentUser.email?.split('@')[0]
                || 'Player'

            // 3. Create Firebase challenge record (real-time coordination only — no money here)
            const expiresAt  = Date.now() + 3 * 60 * 1000
            const newChalRef = push(ref(db, 'challenges'))
            const cid        = newChalRef.key
            await set(newChalRef, {
                cid,
                mongoMatchId,                         // links to MongoDB match
                status:    'pending',
                gameType,
                stake,
                expiresAt,
                createdAt: Date.now(),

                challengerUid:     currentUser.uid,
                challengerName:    myName,
                challengerMongoId: myMongoId || getMongoId(),  // MongoDB _id of challenger

                opponentUid:     challengeTarget.uid,
                opponentName:    challengeTarget.displayName,
                opponentMongoId: challengeTarget.mongoId || null,   // MongoDB _id of opponent

                challengerResult: null,
                opponentResult:   null,
                winner:           null,
                settled:          false,
                disputed:         false,
                roomCode:         null,
            })

            // 4. Notify opponent via backend (Telegram)
            await notifyAPI.send(
                challengeTarget.mongoId,
                `⚔️ <b>New H2H Challenge!</b>\n\n` +
                `<b>${myName}</b> challenged you to <b>${gameType === 'pool' ? '8 Ball Pool 🎱' : 'Checkers ♟️'}</b>\n` +
                `💎 Stake: <b>${stake} diamonds</b> each\n` +
                `🏆 Prize pool: <b>${stake * 2} diamonds</b>\n\n` +
                `⏰ Expires in <b>3 minutes</b>. Open the app to accept!`,
            )

            // 5. Navigate challenger to the lobby
            navigate(`/challenge/${cid}`)
        } catch (err) {
            setChallengeError(err.message || 'Something went wrong. Please try again.')
        }

        setChallengeLoading(false)
        setShowRecordModal(false)
    }, [challengeTarget, currentUser, myMongoId, navigate])

    const handleGoOffline = useCallback(async () => {
        if (!currentUser) return
        const db = getDB()
        const availPlayerRef = ref(db, `availablePlayers/${currentUser.uid}`)
        // Cancel the onDisconnect hook before explicitly removing
        await onDisconnect(availPlayerRef).cancel()
        await Promise.all([
            update(ref(db, `users/${currentUser.uid}/presence`), {
                availableToChallenge: false,
                inMatch: false,
            }),
            remove(availPlayerRef),
        ])
    }, [currentUser])

    // ─── FILTERED PLAYERS ─────────────────────────────────────────────────────
    const filteredPlayers = availablePlayers.filter(p => {
        if (p.uid === currentUser?.uid) return false
        return p.gameType === activeTab
    })
    const isAvailable = userPresence?.availableToChallenge && !userPresence?.inMatch

    // ─── RENDER ───────────────────────────────────────────────────────────────
    return (
        <div style={styles.root}>



            {/* ── Incoming challenge banner ── */}
            {incomingChallenges.map(c => (
                <div key={c.cid} style={styles.challengeBanner} onClick={() => navigate(`/challenge/${c.cid}`)}>
                    <div style={styles.challengeBannerLeft}>
                        <div style={styles.challengeBannerIcon}>⚔️</div>
                        <div style={styles.challengeBannerText}>
                            <div style={styles.challengeBannerTitle}>
                                <strong>{c.challengerName}</strong> challenged you!
                            </div>
                            <div style={styles.challengeBannerSub}>
                                {c.gameType === 'pool' ? '🎱 8 Ball Pool' : '♟️ Checkers'} · 💎 {c.stake} stake
                            </div>
                        </div>
                    </div>
                    <div style={styles.challengeBannerBtn}>View Challenge →</div>
                </div>
            ))}



            {/* ── Online count ── */}
            <div style={styles.onlineCard}>
                <div style={styles.onlineDot} />
                <span style={styles.onlineCount}>{onlinePlayers.length}</span>
                <span style={styles.onlineLabel}>players online</span>
                <div style={styles.avatarRow}>
                    {onlinePlayers.slice(0, 5).map(p => (
                        <div key={p.uid} style={styles.avatar} title={p.displayName}>{initials(p.displayName)}</div>
                    ))}
                </div>
            </div>

            {/* Challenge Players */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <span style={styles.sectionTitle}>Challenge Players</span>
                    <span style={styles.viewAll} onClick={() => navigate('/challenge')}>View All →</span>
                </div>

                <div style={styles.tabs}>
                    {[{ id: 'checkers', label: '♟ Checkers' }, { id: 'pool', label: '● Pool' }].map(tab => (
                        <button key={tab.id} style={activeTab === tab.id ? styles.tabActive : styles.tab} onClick={() => setActiveTab(tab.id)}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {currentUser && (
                    isAvailable ? (
                        <div style={styles.availableBanner}>
                            <span style={styles.availGreenDot} />
                            <span>You're available to challenge ({userPresence.gameType === 'pool' ? '🎱' : '♟️'} · 💎 {userPresence.stake})</span>
                            <button style={styles.stopBtn} onClick={handleGoOffline}>Stop</button>
                        </div>
                    ) : (
                        <button style={styles.setAvailBtn} onClick={() => setShowSetAvail(true)}>
                            <span style={styles.greenDotSmall} />
                            Set yourself available to challenge
                        </button>
                    )
                )}

                {challengeError && <div style={styles.errorMsg}>{challengeError}</div>}

                {filteredPlayers.length === 0 ? (
                    <div style={styles.emptyState}>
                        No {activeTab === 'pool' ? '🎱 Pool' : '♟️ Checkers'} players available right now.
                        <br />Set yourself available to get challenged!
                    </div>
                ) : (
                    filteredPlayers.map(player => (
                        <div key={player.uid} style={styles.playerRow}>
                            <div style={styles.playerAvatar}>{initials(player.displayName)}</div>
                            <div style={styles.playerInfo}>
                                <div style={styles.playerName}>{player.displayName}</div>
                                <div style={styles.playerLevel}>⭐ Level {player.level}</div>
                            </div>
                            <div style={styles.playerStakeCol}>
                                <div style={styles.colLabel}>STAKE</div>
                                <div style={styles.stakeVal}>💎 {player.stake}</div>
                            </div>
                            <div style={styles.playerStatusCol}>
                                <div style={styles.colLabel}>STATUS</div>
                                <div style={styles.statusOnline}>Online</div>
                            </div>
                            <button
                                style={currentUser ? styles.challengeBtn : styles.challengeBtnDisabled}
                                onClick={() => handleChallengeClick(player)}
                                disabled={!currentUser}
                                title={!currentUser ? 'Login to challenge' : `Challenge ${player.displayName}`}
                            >
                                Challenge
                            </button>
                        </div>
                    ))
                )}

                <button style={styles.viewAllBtn} onClick={() => navigate('/challenge')}>View All Players →</button>
            </div>

            {/* Live Tournaments */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <span style={styles.sectionTitle}>Live Tournaments</span>
                    <span style={styles.viewAll} onClick={() => navigate('/tournaments')}>View All →</span>
                </div>
                {tournaments.length === 0 ? (
                    <div style={styles.emptyState}>No open tournaments right now.</div>
                ) : (
                    tournaments.map(t => (
                        <div key={t._id} style={styles.tournamentRow} onClick={() => navigate(`/tournament/${t._id}`)}>
                            <div>
                                <div style={styles.tournamentName}>{t.title || 'Tournament'}</div>
                                <div style={styles.tournamentMeta}>
                                    {t.game === 'pool' ? '🎱' : '♟️'} · {t.players?.length || 0}/{t.maxPlayers} players · 💎 {t.entryFee} entry
                                </div>
                            </div>
                            <div style={styles.joinTournBtn}>Join</div>
                        </div>
                    ))
                )}
            </div>

            {/* Choose Game */}
            <div style={styles.section}>
                <div style={{ ...styles.sectionHeader, marginBottom: 16 }}>
                    <span style={styles.sectionTitle}>Choose Your Game</span>
                </div>
                <div style={styles.gameGrid}>
                    <div style={styles.gameCardPool} onClick={() => navigate('/pool')}>
                        <div style={styles.gameCardOverlay}>
                            <div style={styles.gameCardBadge}>🎱</div>
                            <div style={styles.gameName}>8 Ball Pool</div>
                            <div style={styles.gameDesc}>Challenge players 1v1</div>
                            <div style={styles.gamePlayBtn}>Play Now →</div>
                        </div>
                    </div>
                    <div style={styles.gameCardCheckers} onClick={() => navigate('/checkers')}>
                        <div style={styles.gameCardOverlay}>
                            <div style={styles.gameCardBadge}>♟️</div>
                            <div style={styles.gameName}>Checkers</div>
                            <div style={styles.gameDesc}>Classic strategy game</div>
                            <div style={styles.gamePlayBtn}>Play Now →</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showSetAvail && (
                <SetAvailableModal
                    currentUser={currentUser}
                    myMongoId={myMongoId}
                    onSave={() => setShowSetAvail(false)}
                    onClose={() => setShowSetAvail(false)}
                />
            )}

            {showRecordModal && challengeTarget && (
                <RecordingReminderModal
                    opponent={challengeTarget}
                    stake={challengeTarget.stake}
                    gameType={challengeTarget.gameType}
                    onConfirm={handleConfirmChallenge}
                    onCancel={() => { setShowRecordModal(false); setChallengeTarget(null) }}
                    loading={challengeLoading}
                />
            )}
        </div>
    )
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = {
    root: { minHeight: '100vh', background: '#071309', color: '#e8f5e9', fontFamily: "'Segoe UI', system-ui, sans-serif", maxWidth: 700, margin: '0 auto', padding: '16px 16px 100px' },
    onlineCard: { background: '#0d1f0f', border: '1px solid #1a3a1c', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
    onlineDot: { width: 10, height: 10, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 8px #4ade80' },
    onlineCount: { fontSize: 22, fontWeight: 800, color: '#4ade80' },
    onlineLabel: { fontSize: 13, color: '#4a7a50', flex: 1 },
    avatarRow: { display: 'flex', gap: 4 },
    avatar: { width: 32, height: 32, borderRadius: '50%', background: '#1a3a1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#4ade80', border: '1px solid #2a5a2c' },
    section: { background: '#0d1f0f', border: '1px solid #1a3a1c', borderRadius: 12, padding: '20px 16px', marginBottom: 16 },
    sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    sectionTitle: { fontSize: 17, fontWeight: 700, color: '#e8f5e9' },
    viewAll: { fontSize: 13, color: '#4ade80', cursor: 'pointer' },
    tabs: { display: 'flex', gap: 8, marginBottom: 14 },
    tab: { padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1px solid #1a3a1c', background: 'transparent', color: '#4a7a50', cursor: 'pointer' },
    tabActive: { padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1px solid #4ade80', background: '#1a3a1c', color: '#4ade80', cursor: 'pointer' },
    setAvailBtn: { width: '100%', padding: '12px 16px', border: '1px dashed #2a5a2c', borderRadius: 10, background: 'transparent', color: '#4ade80', cursor: 'pointer', fontSize: 14, fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 },
    greenDotSmall: { width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block' },
    availableBanner: { display: 'flex', alignItems: 'center', gap: 8, background: '#0a2a0c', border: '1px solid #2a5a2c', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#4ade80' },
    availGreenDot: { width: 8, height: 8, borderRadius: '50%', background: '#4ade80', flexShrink: 0 },
    stopBtn: { marginLeft: 'auto', padding: '4px 12px', border: '1px solid #f44336', borderRadius: 6, background: 'transparent', color: '#f44336', cursor: 'pointer', fontSize: 12 },
    playerRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #1a3a1c' },
    playerAvatar: { width: 40, height: 40, borderRadius: '50%', background: '#1a3a1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#4ade80', flexShrink: 0 },
    playerInfo: { flex: 1, minWidth: 0 },
    playerName: { fontSize: 15, fontWeight: 600, color: '#e8f5e9' },
    playerLevel: { fontSize: 12, color: '#4a7a50' },
    playerStakeCol: { textAlign: 'center', minWidth: 60 },
    playerStatusCol: { textAlign: 'center', minWidth: 60 },
    colLabel: { fontSize: 9, color: '#3a5a3c', letterSpacing: '0.08em', marginBottom: 2 },
    stakeVal: { fontSize: 13, fontWeight: 700, color: '#e8f5e9' },
    statusOnline: { fontSize: 11, fontWeight: 600, color: '#4ade80', border: '1px solid #2a5a2c', borderRadius: 10, padding: '2px 8px', display: 'inline-block' },
    challengeBtn: { padding: '8px 14px', borderRadius: 8, background: '#1a3a1c', border: '1px solid #2a6a2c', color: '#4ade80', fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0 },
    challengeBtnDisabled: { padding: '8px 14px', borderRadius: 8, background: '#111', border: '1px solid #222', color: '#444', fontWeight: 700, fontSize: 13, cursor: 'not-allowed', flexShrink: 0 },
    viewAllBtn: { width: '100%', marginTop: 12, padding: '10px', background: 'transparent', border: '1px solid #1a3a1c', color: '#4a7a50', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
    emptyState: { textAlign: 'center', color: '#3a5a3c', padding: '24px 0', fontSize: 14, lineHeight: 1.6 },
    tournamentRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #1a3a1c', cursor: 'pointer' },
    tournamentName: { fontSize: 14, fontWeight: 600, color: '#e8f5e9', marginBottom: 4 },
    tournamentMeta: { fontSize: 12, color: '#4a7a50' },
    joinTournBtn: {
        padding: '6px 14px', borderRadius: 6,
        background: '#4ade80', color: '#071309', fontWeight: 700, fontSize: 12,
    },

    // Games grid
    gameGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    gameCardPool: {
        height: 160,
        borderRadius: 14,
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(135deg, #0a2a10 0%, #0d3a14 40%, #1a5a20 100%)',
        border: '1px solid #1a4a1c',
        boxShadow: '0 4px 20px rgba(74,222,128,0.08)',
    },
    gameCardCheckers: {
        height: 160,
        borderRadius: 14,
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(135deg, #0a1a2a 0%, #0d2a3a 40%, #1a3a5a 100%)',
        border: '1px solid #1a3a5a',
        boxShadow: '0 4px 20px rgba(74,200,255,0.06)',
    },
    gameCardOverlay: {
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '16px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)',
    },
    gameCardBadge: {
        fontSize: 32, marginBottom: 6,
        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
    },
    gameName: { fontSize: 16, fontWeight: 800, color: '#e8f5e9', marginBottom: 2, letterSpacing: '0.02em' },
    gameDesc: { fontSize: 11, color: '#8abf8a', marginBottom: 8 },
    gamePlayBtn: {
        display: 'inline-block',
        fontSize: 12, fontWeight: 700, color: '#4ade80',
        background: 'rgba(74,222,128,0.15)',
        border: '1px solid rgba(74,222,128,0.3)',
        borderRadius: 20, padding: '4px 12px',
        width: 'fit-content',
    },
    gameEmoji: { fontSize: 36, marginBottom: 8 },

    // Modal
    overlay: {
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
    },
    modal: {
        background: '#0d1f0f',
        border: '1px solid #2a5a2c',
        borderRadius: 16, padding: '28px 24px',
        width: '100%', maxWidth: 420,
        maxHeight: '90vh', overflowY: 'auto',
    },
    modalIcon: { fontSize: 40, textAlign: 'center', marginBottom: 12 },
    modalTitle: { fontSize: 20, fontWeight: 800, color: '#e8f5e9', textAlign: 'center', margin: '0 0 8px' },
    modalBody: { fontSize: 14, color: '#4a7a50', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.5 },
    reminderBox: { background: '#1a2a0a', border: '1px solid #3a6a20', borderRadius: 10, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 20 },
    reminderDot: { fontSize: 16, flexShrink: 0 },
    reminderText: { fontSize: 13, color: '#8aaa60', lineHeight: 1.5 },
    matchPreview: { background: '#081508', border: '1px solid #1a3a1c', borderRadius: 10, padding: '14px 16px', marginBottom: 16 },
    matchPreviewRow: { display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #111' },
    previewLabel: { fontSize: 13, color: '#4a7a50' },
    previewValue: { fontSize: 13, fontWeight: 600, color: '#e8f5e9' },
    confirmNote: { fontSize: 12, color: '#3a5a3c', textAlign: 'center', lineHeight: 1.5, marginBottom: 20 },
    modalButtons: { display: 'flex', gap: 10 },
    cancelBtn: { flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #1a3a1c', background: 'transparent', color: '#4a7a50', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
    confirmBtn: { flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: '#4ade80', color: '#071309', fontWeight: 800, cursor: 'pointer', fontSize: 14 },
    fieldGroup: { marginBottom: 16 },
    fieldLabel: { fontSize: 12, color: '#4a7a50', display: 'block', marginBottom: 8, letterSpacing: '0.05em' },
    gameToggle: { display: 'flex', gap: 8 },
    gameToggleBtn: { flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #1a3a1c', background: 'transparent', color: '#4a7a50', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    gameToggleActive: { flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #4ade80', background: '#1a3a1c', color: '#4ade80', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    presetRow: { display: 'flex', gap: 8, marginBottom: 10 },
    presetBtn: {
        flex: 1, padding: '8px 4px', borderRadius: 8, fontSize: 12, fontWeight: 600,
        border: '1px solid #1a3a1c', background: 'transparent', color: '#4a7a50', cursor: 'pointer',
    },
    presetActive: {
        flex: 1, padding: '8px 4px', borderRadius: 8, fontSize: 12, fontWeight: 600,
        border: '1px solid #4ade80', background: '#1a3a1c', color: '#4ade80', cursor: 'pointer',
    },
    input: {
        width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
        border: '1px solid #1a3a1c', background: '#081508', color: '#e8f5e9',
        boxSizing: 'border-box',
    },

    // Top bar
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 8px' },
    topBarLogo: { fontSize: 16, fontWeight: 800, color: '#4ade80' },
    topBarRight: { display: 'flex', alignItems: 'center', gap: 12 },
    diamondBadge: { fontSize: 14, fontWeight: 700, color: '#4ade80', background: '#0a2a0c', padding: '5px 12px', borderRadius: 20, border: '1px solid #2a5a2c' },
    bellBtn: { position: 'relative', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '4px 8px' },
    bellDot: { position: 'absolute', top: 0, right: 0, background: '#f44336', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },

    // Incoming challenge banner
    challengeBanner: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, background: 'linear-gradient(135deg, #0a2a0c, #0d3a10)',
        border: '1px solid #1a5a1c', borderRadius: 12, padding: '14px 16px',
        cursor: 'pointer', marginBottom: 8,
        boxShadow: '0 2px 12px rgba(74,222,128,0.12)',
    },
    challengeBannerLeft: { display: 'flex', alignItems: 'center', gap: 12, flex: 1 },
    challengeBannerIcon: { fontSize: 22, flexShrink: 0 },
    challengeBannerText: { flex: 1 },
    challengeBannerTitle: { fontSize: 14, fontWeight: 700, color: '#e8f5e9', marginBottom: 2 },
    challengeBannerSub: { fontSize: 12, color: '#4a9a50' },
    challengeBannerBtn: {
        fontSize: 12, fontWeight: 800, color: '#040d06',
        background: '#4ade80', border: 'none',
        borderRadius: 20, padding: '6px 14px',
        flexShrink: 0, whiteSpace: 'nowrap',
    },
    challengeBannerAction: { fontSize: 13, fontWeight: 700, color: '#4ade80', flexShrink: 0 },

    // Notification panel
    notifPanel: { background: '#0d1f0f', border: '1px solid #1a3a1c', borderRadius: 12, marginBottom: 12, overflow: 'hidden' },
    notifHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #1a3a1c' },
    notifTitle: { fontSize: 14, fontWeight: 700, color: '#e8f5e9' },
    notifClose: { background: 'none', border: 'none', color: '#4a7a50', fontSize: 16, cursor: 'pointer' },
    notifEmpty: { padding: '20px 16px', color: '#3a5a3c', fontSize: 13, textAlign: 'center' },
    notifItem: { display: 'flex', flexDirection: 'column', gap: 2, padding: '12px 16px', borderBottom: '1px solid #0a1a0c', cursor: 'pointer' },
    notifItemTitle: { fontSize: 13, fontWeight: 600, color: '#e8f5e9' },
    notifItemSub: { fontSize: 12, color: '#4a7a50' },
    notifItemTime: { fontSize: 11, color: '#2a5a2c', marginTop: 2 },

    errorMsg: {
        background: '#2a0a0a', border: '1px solid #f44336',
        borderRadius: 8, padding: '10px 14px',
        color: '#f44336', fontSize: 13, marginBottom: 12,
    },
}
